import { useEffect, useRef } from 'react';
import { useConsoleLogStore } from '../store/consoleLogStore.js';
import { initFetchInterceptor, FETCH_LOG_EVENT } from '../interceptors/fetchInterceptor.js';
import { FlowObserverPanelEngine } from '../engine/FlowObserverPanelEngine.js';
import type { FetchXhrLogEntry, SocketLogEntry } from '../types/console-log.types.js';

const TAB_ID = 'console-log';
let _skSeq = 0;

function nextSocketID(): string {
  return `sk-${Date.now()}-${++_skSeq}`;
}

/**
 * Mounts in WorkflowEditor (always).
 * Becomes active only when consoleLog.enabled === true (persisted in localStorage).
 * Intercepts fetch/XHR calls and socket events, pushes to the console-log observer tab.
 */
export function useConsoleLogCapture(): void {
  const enabled = useConsoleLogStore((s) => s.enabled);
  const badgeRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    initFetchInterceptor(); // idempotent singleton

    const handleFetch = (e: Event): void => {
      const entry = (e as CustomEvent<FetchXhrLogEntry>).detail;
      if (entry.phase === 'error') {
        badgeRef.current += 1;
        FlowObserverPanelEngine.setTabBadge(TAB_ID, badgeRef.current);
      }
      FlowObserverPanelEngine.pushData(TAB_ID, entry);
    };

    // Incoming SignalR messages dispatched by useExecutionSignalR as 'execution-event'
    const handleExecutionEvent = (e: Event): void => {
      const detail = (e as CustomEvent).detail as Record<string, unknown> | undefined;
      const envelope = detail?.dataEnvelope as Record<string, unknown> | undefined;
      const meta = envelope?.metadata as Record<string, unknown> | undefined;
      const msgType = meta?.messageType as Record<string, unknown> | undefined;

      const entry: SocketLogEntry = {
        _type: 'socket',
        id: nextSocketID(),
        timestamp: new Date().toISOString(),
        direction: 'incoming',
        event: String(detail?.type ?? 'ServerEvent'),
        topic: msgType?.subType != null ? String(msgType.subType) : String(detail?.type ?? ''),
        subType: msgType?.subType != null ? String(msgType.subType) : undefined,
        payload: detail,
      };
      FlowObserverPanelEngine.pushData(TAB_ID, entry);
    };

    // Outgoing SignalR calls dispatched by useExecutionSignalR as 'socket-log-outgoing'
    const handleSocketOutgoing = (e: Event): void => {
      const entry = (e as CustomEvent<SocketLogEntry>).detail;
      FlowObserverPanelEngine.pushData(TAB_ID, entry);
    };

    window.addEventListener(FETCH_LOG_EVENT, handleFetch);
    window.addEventListener('execution-event', handleExecutionEvent);
    window.addEventListener('socket-log-outgoing', handleSocketOutgoing);

    return () => {
      window.removeEventListener(FETCH_LOG_EVENT, handleFetch);
      window.removeEventListener('execution-event', handleExecutionEvent);
      window.removeEventListener('socket-log-outgoing', handleSocketOutgoing);
    };
  }, [enabled]);
}
